'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useEffect, useRef, useState, useTransition } from 'react'
import { useWorkspaceScopedLoader, type WorkspaceScopedLoadContext } from '@kiao-voice/hooks'
import { useTranslation } from 'react-i18next'
import { Pencil } from 'lucide-react'
import {
  Alert,
  Button,
  ButtonLink,
  Field,
  Input,
  KiaoModal,
  Picker,
  Switch,
  useKiaoConfirm,
} from '@kiao-voice/ui-kit'
import { WorkspacePageHeader } from '@/components/workspace-indicator'
import {
  BillingApiError,
  createApiKey,
  deleteApiKey,
  fetchApiKeys,
  updateApiKey,
  type ApiKeyGrantableResource,
  type ApiKeyScope,
  type ApiKeySummary,
} from '@/features/billing/api-client'
import { readStoredToken } from '@/features/auth/auth-storage'
import {
  EmailVerificationStep,
  type EmailVerificationStepProps,
} from '@/features/auth/email-verification'
import { EMAIL_VERIFICATION_REQUIRED_ERROR } from '@kiao-voice/core'
import { useInlineEditable } from '@/components/use-inline-editable'
import { getWebApiDocsUrl } from '@/lib/api'

/**
 * API 金鑰 (/app/api-keys) — 與 /app/account 同一套版式語彙。
 *
 * 頁面是一份憑證清單，不是表單：頁首右上一顆「建立金鑰」開 <KiaoModal>，主體是
 * 髮絲線金鑰列（沿用 /account 付款方式卡片列的語法）。權限只在 modal 裡呈現，避免
 * 長度不定的 scope 撐破固定列寬。剛建立的金鑰是這頁
 * 唯一一次看得到的東西，所以獨立成頁首的墨黑帶而不是一則提示。
 */

/**
 * The API surfaces grantable from this page come from the server
 * (`ApiKeyListResponse.grantableResources`), not a constant here: `voice-input`
 * and `tts` are per-org rollouts, and the org flags that open them live only in
 * the backend. A stored grant for a resource NOT in that list is deliberately
 * un-editable and preserved verbatim — see `buildUpdatedScopes`.
 *
 * Until the first fetch resolves, assume the one resource every org can
 * self-serve. Guessing wider would flash a toggle the org may not have.
 */
const DEFAULT_GRANTABLE_RESOURCES: ApiKeyGrantableResource[] = ['batch-transcriptions']
type ScopeResource = ApiKeyGrantableResource
type ExpiryPreset = '30' | '90' | '365' | 'never'

type Translate = ReturnType<typeof useTranslation<'web'>>['t']

interface Props {
  getTokenFn?: () => string | null
  fetchApiKeysFn?: typeof fetchApiKeys
  createApiKeyFn?: typeof createApiKey
  updateApiKeyFn?: typeof updateApiKey
  deleteApiKeyFn?: typeof deleteApiKey
  requestVerificationFn?: EmailVerificationStepProps['requestFn']
  verifyFn?: EmailVerificationStepProps['verifyFn']
}

/** The machine code in an API error body (`{ error }`), when there is one. */
function errorCodeOf(error: BillingApiError): string | null {
  const body = error.body
  return body && typeof body === 'object' && 'error' in body
    ? String((body as { error: unknown }).error)
    : null
}

export function ApiKeysPage({
  getTokenFn = readStoredToken,
  fetchApiKeysFn = fetchApiKeys,
  createApiKeyFn = createApiKey,
  updateApiKeyFn = updateApiKey,
  deleteApiKeyFn = deleteApiKey,
  requestVerificationFn,
  verifyFn,
}: Props = {}) {
  const { t } = useTranslation('web')
  const confirm = useKiaoConfirm()
  const [keys, setKeys] = useState<ApiKeySummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Create dialog state.
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newResources, setNewResources] = useState<Set<ScopeResource>>(
    () => new Set<ScopeResource>(['batch-transcriptions'])
  )
  const [newExpiry, setNewExpiry] = useState<ExpiryPreset>('90')
  // Validation / API errors raised INSIDE the create dialog stay in the dialog —
  // a <dialog> opened with showModal() makes the page behind it inert, so a
  // page-level alert would be invisible to the person who triggered it.
  const [createError, setCreateError] = useState<{ name?: string; form?: string }>({})
  /** The create call answered `email_verification_required`; the dialog shows the inbox check. */
  const [verifyingCreate, setVerifyingCreate] = useState(false)
  const [createdRaw, setCreatedRaw] = useState<string | null>(null)
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle')
  const [scopeModalKey, setScopeModalKey] = useState<ApiKeySummary | null>(null)
  const [scopeDraft, setScopeDraft] = useState<Set<ScopeResource>>(() => new Set<ScopeResource>())
  const [scopeError, setScopeError] = useState<string | null>(null)
  const [grantableResources, setGrantableResources] = useState<ApiKeyGrantableResource[]>(
    DEFAULT_GRANTABLE_RESOURCES
  )
  const [isPending, startTransition] = useTransition()

  const copyKey = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopyStatus('copied')
      // Revert the "Copied" label back to "Copy" after 2s so a second
      // copy click is obviously a fresh action (and not stale state).
      window.setTimeout(() => setCopyStatus('idle'), 2000)
    } catch (caughtError) {
      reportHandledException(caughtError, { source: 'app/app/api-keys/api-keys-page.tsx' })
      // Permissions revoked / non-secure context / clipboard API
      // unavailable. Tell the user instead of silently failing — the key is
      // rendered as selectable text, so they can still copy it by hand.
      setError(t('apiKeys.copyFailed'))
    }
  }

  const loadWorkspace = useCallback(
    async ({ isCurrent }: WorkspaceScopedLoadContext) => {
      const token = getTokenFn()
      if (!token) {
        if (isCurrent()) setLoading(false)
        return
      }
      try {
        const res = await fetchApiKeysFn()
        if (isCurrent()) {
          setKeys(res.apiKeys)
          setGrantableResources(res.grantableResources)
          setScopeModalKey(null)
          setError(null)
        }
      } catch (cause) {
        reportHandledException(cause, { source: 'app/app/api-keys/api-keys-page.tsx' })
        if (isCurrent()) {
          setError(cause instanceof BillingApiError ? cause.message : t('apiKeys.errorLoad'))
        }
      } finally {
        if (isCurrent()) setLoading(false)
      }
    },
    [fetchApiKeysFn, getTokenFn, t]
  )

  const clearWorkspace = useCallback(() => {
    setKeys([])
    // Capability flags are per-ORG, so they must not survive a workspace switch.
    setGrantableResources(DEFAULT_GRANTABLE_RESOURCES)
    setLoading(true)
    setError(null)
    setCreateOpen(false)
    setCreateError({})
    setCreatedRaw(null)
    setCopyStatus('idle')
    setScopeModalKey(null)
    setScopeError(null)
  }, [])

  const { captureWorkspace } = useWorkspaceScopedLoader({
    load: loadWorkspace,
    clear: clearWorkspace,
  })

  const openCreate = () => {
    setError(null)
    setCreateError({})
    setNewName('')
    setNewResources(new Set<ScopeResource>(['batch-transcriptions']))
    setNewExpiry('90')
    setCreateOpen(true)
  }

  const toggleNewResource = (resource: ScopeResource) => {
    setNewResources((prev) => toggled(prev, resource))
  }

  const openScopeModal = (apiKey: ApiKeySummary) => {
    setError(null)
    setScopeError(null)
    setScopeModalKey(apiKey)
    setScopeDraft(scopesToResources(apiKey.scopes, grantableResources))
  }

  const toggleDraftResource = (resource: ScopeResource) => {
    setScopeDraft((prev) => toggled(prev, resource))
  }

  const handleCreate = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const name = newName.trim()
    if (!name) {
      setCreateError({ name: t('apiKeys.errorNameRequired') })
      return
    }
    if (newResources.size === 0) {
      setCreateError({ form: t('apiKeys.errorScopeRequired') })
      return
    }
    setCreateError({})
    submitCreate()
  }

  /** Send the dialog's current values; shared by the form and the post-verification retry. */
  const submitCreate = () => {
    const token = getTokenFn()
    if (!token) return
    const name = newName.trim()
    // Each selected resource grants write access (write implies read).
    const scopes = Array.from(newResources, (resource) => ({ resource, action: 'write' }))
    const expiresAt = expiryPresetToIso(newExpiry)
    const isCurrentWorkspace = captureWorkspace()
    startTransition(async () => {
      try {
        const { apiKey, rawKey } = await createApiKeyFn({
          name,
          scopes,
          ...(expiresAt ? { expiresAt } : {}),
        })
        if (!isCurrentWorkspace()) return
        setKeys((prev) => [apiKey, ...prev])
        setCreatedRaw(rawKey)
        setCopyStatus('idle')
        setCreateOpen(false)
        setNewName('')
      } catch (cause) {
        reportHandledException(cause, { source: 'app/app/api-keys/api-keys-page.tsx' })
        if (!isCurrentWorkspace()) return
        // Minting needs a proven mailbox: verify in place inside the dialog,
        // then send the same values again.
        if (
          cause instanceof BillingApiError &&
          errorCodeOf(cause) === EMAIL_VERIFICATION_REQUIRED_ERROR
        ) {
          setVerifyingCreate(true)
          return
        }
        // Keep the dialog open with the values intact so the user can retry.
        setCreateError({
          form: cause instanceof BillingApiError ? cause.message : t('apiKeys.errorCreate'),
        })
      }
    })
  }

  const handleUpdateScopes = () => {
    const apiKey = scopeModalKey
    if (!apiKey) return
    if (scopeDraft.size === 0) {
      setScopeError(t('apiKeys.errorScopeRequired'))
      return
    }
    setScopeError(null)
    const token = getTokenFn()
    if (!token) return
    const existingScopes = keys.find((key) => key.id === apiKey.id)?.scopes ?? []
    const scopes = buildUpdatedScopes(existingScopes, scopeDraft, grantableResources)
    const isCurrentWorkspace = captureWorkspace()
    startTransition(async () => {
      try {
        const { apiKey: updatedKey } = await updateApiKeyFn(apiKey.id, { scopes })
        if (!isCurrentWorkspace()) return
        setKeys((prev) => prev.map((key) => (key.id === updatedKey.id ? updatedKey : key)))
        setScopeModalKey(null)
      } catch (cause) {
        reportHandledException(cause, { source: 'app/app/api-keys/api-keys-page.tsx' })
        if (!isCurrentWorkspace()) return
        setScopeError(cause instanceof BillingApiError ? cause.message : t('apiKeys.errorUpdate'))
      }
    })
  }

  const handleRename = async (id: number, name: string) => {
    const token = getTokenFn()
    if (!token) return
    const isCurrentWorkspace = captureWorkspace()
    try {
      const { apiKey } = await updateApiKeyFn(id, { name })
      if (!isCurrentWorkspace()) return
      setKeys((prev) => prev.map((key) => (key.id === apiKey.id ? apiKey : key)))
    } catch (cause) {
      reportHandledException(cause, { source: 'app/app/api-keys/api-keys-page.tsx' })
      if (!isCurrentWorkspace()) return
      setError(cause instanceof BillingApiError ? cause.message : t('apiKeys.errorUpdate'))
      throw cause
    }
  }

  const handleDelete = async (id: number) => {
    const isCurrentWorkspace = captureWorkspace()
    const ok = await confirm({
      title: t('apiKeys.revokeTitle'),
      message: t('apiKeys.revokeConfirm'),
      confirmText: t('apiKeys.revoke'),
      cancelText: t('common:cancel'),
    })
    if (!ok || !isCurrentWorkspace()) return
    setError(null)
    const token = getTokenFn()
    if (!token) return
    startTransition(async () => {
      try {
        await deleteApiKeyFn(id)
        if (!isCurrentWorkspace()) return
        setKeys((prev) => prev.filter((k) => k.id !== id))
        setScopeModalKey((prev) => (prev?.id === id ? null : prev))
      } catch (cause) {
        reportHandledException(cause, { source: 'app/app/api-keys/api-keys-page.tsx' })
        if (!isCurrentWorkspace()) return
        setError(cause instanceof BillingApiError ? cause.message : t('apiKeys.errorRevoke'))
      }
    })
  }

  return (
    <div data-testid="api-keys-page">
      <WorkspacePageHeader
        className="akey-page-head"
        title={t('apiKeys.heading')}
        subtitle={t('apiKeys.subhead')}
        actions={
          <div className="akey-head-actions" data-testid="api-keys-header-actions">
            <ButtonLink
              variant="ghost"
              size="sm"
              data-testid="api-keys-docs-link"
              href={getWebApiDocsUrl()}
              target="_blank"
              rel="noreferrer"
            >
              {t('apiKeys.apiDocs')} <span aria-hidden="true">↗</span>
            </ButtonLink>
            <Button size="sm" data-testid="api-keys-create-open" onClick={openCreate}>
              {t('apiKeys.createKey')}
            </Button>
          </div>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      {createdRaw ? (
        <section className="akey-new" data-testid="api-keys-secret">
          <h2>{t('apiKeys.saveKeyNow')}</h2>
          <p className="akey-new-note">{t('apiKeys.saveKeyBody')}</p>
          <div className="copyrow">
            <code className="input num akey-secret">{createdRaw}</code>
            <button
              type="button"
              className="btn btn-onink"
              onClick={() => void copyKey(createdRaw)}
            >
              {copyStatus === 'copied' ? t('apiKeys.copied') : t('apiKeys.copy')}
            </button>
          </div>
          <button
            type="button"
            className="btn btn-inverse btn-sm"
            onClick={() => setCreatedRaw(null)}
          >
            {t('apiKeys.iSavedIt')}
          </button>
        </section>
      ) : null}

      {loading ? (
        <p className="note">{t('common:loading')}</p>
      ) : keys.length === 0 ? (
        <p className="note" data-testid="api-keys-empty">
          {t('apiKeys.noKeysYet')}
        </p>
      ) : (
        <>
          {/* Scopes deliberately render only in the permission modal: an unbounded
              list of grants would otherwise wrap or truncate in this fixed-width row. */}
          <div className="akey-list" data-testid="api-keys-list">
            {keys.map((key) => (
              <ApiKeyRow
                key={key.id}
                apiKey={key}
                onRename={handleRename}
                onOpenScopeModal={openScopeModal}
                onDelete={handleDelete}
                t={t}
              />
            ))}
          </div>
        </>
      )}

      <KiaoModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false)
          setVerifyingCreate(false)
        }}
        title={t('apiKeys.createKey')}
        subtitle={t('apiKeys.createSubhead')}
        closeLabel={t('common:dismiss')}
      >
        {verifyingCreate ? (
          <EmailVerificationStep
            intro={t('apiKeys.verifyDesc')}
            submitLabel={t('apiKeys.verifyAndCreate')}
            onVerified={() => {
              // The code is spent; the step ends and the create reports its own result.
              setVerifyingCreate(false)
              submitCreate()
            }}
            onCancel={() => setVerifyingCreate(false)}
            testIdPrefix="api-keys-verify"
            requestFn={requestVerificationFn}
            verifyFn={verifyFn}
          />
        ) : (
          <form className="akey-form" onSubmit={handleCreate} data-testid="api-keys-create-form">
            <Field
              label={t('apiKeys.nameLabel')}
              htmlFor="api-key-name"
              error={createError.name ?? null}
            >
              <Input
                id="api-key-name"
                type="text"
                value={newName}
                onChange={(event) => {
                  setNewName(event.target.value)
                  if (createError.name) setCreateError({})
                }}
                maxLength={64}
                placeholder={t('apiKeys.namePlaceholder')}
              />
            </Field>
            <Field label={t('apiKeys.expiryLabel')} htmlFor="api-keys-expiry">
              <Picker
                id="api-keys-expiry"
                label={t('apiKeys.expiryLabel')}
                value={newExpiry}
                onChange={(value) => setNewExpiry(value as ExpiryPreset)}
                options={[
                  { value: '30', label: t('apiKeys.expiry30') },
                  { value: '90', label: t('apiKeys.expiry90') },
                  { value: '365', label: t('apiKeys.expiryYear') },
                  { value: 'never', label: t('apiKeys.expiryNever') },
                ]}
              />
            </Field>
            <ScopeSwitchRows
              available={grantableResources}
              resources={newResources}
              onToggle={toggleNewResource}
              t={t}
            />
            {createError.form ? <Alert>{createError.form}</Alert> : null}
            <div className="bact">
              <Button variant="secondary" onClick={() => setCreateOpen(false)}>
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? t('apiKeys.creating') : t('apiKeys.createKey')}
              </Button>
            </div>
          </form>
        )}
      </KiaoModal>

      <KiaoModal
        open={scopeModalKey !== null}
        onClose={() => setScopeModalKey(null)}
        title={t('apiKeys.editScopes')}
        subtitle={scopeModalKey?.name}
        closeLabel={t('common:dismiss')}
      >
        <form
          className="akey-form"
          onSubmit={(event) => {
            event.preventDefault()
            handleUpdateScopes()
          }}
        >
          <ScopeSwitchRows
            available={grantableResources}
            resources={scopeDraft}
            onToggle={toggleDraftResource}
            disabled={isPending}
            t={t}
          />
          {scopeError ? <Alert>{scopeError}</Alert> : null}
          <div className="bact">
            <Button type="submit" disabled={isPending}>
              {isPending ? t('apiKeys.saving') : t('common:save')}
            </Button>
          </div>
        </form>
      </KiaoModal>
    </div>
  )
}

interface ApiKeyRowProps {
  apiKey: ApiKeySummary
  onRename: (id: number, name: string) => Promise<void>
  onOpenScopeModal: (apiKey: ApiKeySummary) => void
  onDelete: (keyId: number) => void
  t: Translate
}

function ApiKeyRow({ apiKey, onRename, onOpenScopeModal, onDelete, t }: ApiKeyRowProps) {
  const [renaming, setRenaming] = useState(false)
  const pencilRef = useRef<HTMLButtonElement>(null)
  const restoreFocus = useRef(false)
  const editable = useInlineEditable<HTMLDivElement>(
    apiKey.name,
    (name) => onRename(apiKey.id, name),
    undefined,
    {
      onClose: () => {
        if (renaming) restoreFocus.current = true
        setRenaming(false)
      },
      maxLength: 64,
    }
  )

  useEffect(() => {
    if (renaming) {
      editable.focusEnd()
    } else if (restoreFocus.current) {
      restoreFocus.current = false
      pencilRef.current?.focus()
    }
  }, [renaming])

  const expired = isExpired(apiKey.expiresAt)
  return (
    <div className="akey-row" data-testid={`api-key-${apiKey.id}`}>
      <div className="akey-id">
        <div className="akey-nameline">
          <div
            ref={editable.ref}
            className="akey-name"
            data-testid={`api-key-name-${apiKey.id}`}
            aria-label={t('apiKeys.rename')}
            {...editable.elementProps}
          />
          {expired ? <span className="akey-chip">{t('apiKeys.expired')}</span> : null}
          <button
            ref={pencilRef}
            type="button"
            className="akey-edit"
            aria-label={t('apiKeys.rename')}
            title={t('apiKeys.rename')}
            onClick={() => setRenaming(true)}
          >
            <Pencil aria-hidden="true" />
          </button>
        </div>
        <div className="akey-meta">
          <span>{apiKey.keyPrefix}…</span>
          {' · '}
          {t('apiKeys.createdOn', { date: formatDate(apiKey.createdAt) })}
          {' · '}
          {apiKey.expiresAt
            ? t('apiKeys.expiresOn', { date: formatDate(apiKey.expiresAt) })
            : t('apiKeys.neverExpires')}
        </div>
      </div>
      <div className="akey-used">
        {apiKey.lastUsedAt
          ? t('apiKeys.lastUsed', { date: formatDate(apiKey.lastUsedAt) })
          : t('apiKeys.neverUsed')}
      </div>
      <div className="akey-acts">
        <button type="button" className="akey-act" onClick={() => onOpenScopeModal(apiKey)}>
          {t('apiKeys.editScopes')}
        </button>
        <button type="button" className="akey-act" onClick={() => void onDelete(apiKey.id)}>
          {t('apiKeys.revoke')}
        </button>
      </div>
    </div>
  )
}

function ScopeSwitchRows({
  available,
  resources,
  onToggle,
  disabled = false,
  t,
}: {
  available: ApiKeyGrantableResource[]
  resources: Set<ScopeResource>
  onToggle: (resource: ScopeResource) => void
  disabled?: boolean
  t: Translate
}) {
  return (
    <div className="akey-scope-set" data-testid="api-keys-scope-set">
      {available.map((resource) => (
        <div className="akey-swrow" key={resource}>
          <div>
            <div>{t(`apiKeys.scope.${resource}`)}</div>
            <p>{t(`apiKeys.scopeDescription.${resource}`)}</p>
          </div>
          <Switch
            checked={resources.has(resource)}
            disabled={disabled}
            aria-label={t(`apiKeys.scope.${resource}`)}
            onChange={() => onToggle(resource)}
          />
        </div>
      ))}
    </div>
  )
}

/** ISO timestamp → YYYY-MM-DD, the `.num` date format used across the account pages. */
function formatDate(iso: string): string {
  return iso.slice(0, 10)
}

function toggled(prev: Set<ScopeResource>, resource: ScopeResource): Set<ScopeResource> {
  const next = new Set(prev)
  if (next.has(resource)) next.delete(resource)
  else next.add(resource)
  return next
}

function scopesToResources(
  scopes: ApiKeyScope[],
  available: ApiKeyGrantableResource[]
): Set<ScopeResource> {
  const resources = new Set<ScopeResource>()
  for (const resource of available) {
    if (scopes.some((scope) => scope.resource === resource || scope.resource === '*')) {
      resources.add(resource)
    }
  }
  return resources
}

function expiryPresetToIso(preset: ExpiryPreset): string | undefined {
  if (preset === 'never') return undefined
  const expiresAt = new Date()
  expiresAt.setUTCDate(expiresAt.getUTCDate() + Number(preset))
  return expiresAt.toISOString()
}

function isExpired(expiresAt: string | null): boolean {
  return expiresAt !== null && new Date(expiresAt).getTime() < Date.now()
}

/**
 * The full scope list to PATCH. The endpoint REPLACES `scopes` wholesale, so
 * anything the editor cannot represent has to be carried across by hand.
 *
 * "Managed" is what this org may currently grant, NOT every resource that
 * exists: a key holding `tts` while the org's TTS rollout is closed shows no
 * toggle for it, and dropping it here would silently revoke a partner's live
 * integration on an unrelated save. Such a grant is therefore unmanaged and
 * copied through untouched.
 *
 * A toggle says WHETHER the key reaches a resource, never with which action, so
 * a resource it already holds keeps its stored grants verbatim. Rebuilding every
 * managed grant as `write` would silently escalate a deliberately read-only
 * integration on an unrelated save — `tts:read` lists voices and downloads
 * clips, `tts:write` submits synthesis jobs and spends the org's credits.
 * Changing an action is an ops action (`api-keys:create`), not a side effect of
 * pressing Save. Only a newly switched-on resource is granted `write`.
 *
 * A `*` wildcard is the one grant that cannot survive: the toggles have no way
 * to express "everything, including resources added later", so saving expands it
 * into the explicit set shown. Pre-existing behavior, and the wider `available`
 * list makes that expansion lossier the fewer capabilities an org has open. No
 * such key exists today; expanding it losslessly needs the client to know every
 * resource that exists, which is exactly the knowledge this page stopped holding.
 */
function buildUpdatedScopes(
  existingScopes: ApiKeyScope[],
  resources: Set<ScopeResource>,
  available: ApiKeyGrantableResource[]
): ApiKeyScope[] {
  const managedScopes = Array.from(resources).flatMap((resource) => {
    const stored = existingScopes.filter((scope) => scope.resource === resource)
    return stored.length > 0 ? stored : [{ resource, action: 'write' }]
  })
  const unmanagedScopes = existingScopes.filter(
    (scope) =>
      scope.resource !== '*' && !available.includes(scope.resource as ApiKeyGrantableResource)
  )
  return [...managedScopes, ...unmanagedScopes]
}
